import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    FaCalendarAlt, 
    FaClock, 
    FaVideo,
    FaPlayCircle,
    FaExternalLinkAlt,
    FaExclamationTriangle,
    FaFileAlt,
    FaRegClock,
    FaChevronDown,
    FaChevronRight,
    FaBars,
    FaTimes,
    FaList,
    FaBookOpen,
    FaHome,
    FaTachometerAlt,
    FaChartBar,
    FaBriefcase,
    FaDollarSign,
    FaBell,
    FaCompass,
    FaCog,
    FaUsers,
    FaUserEdit,
    FaUserPlus,
    FaBuilding,
    FaAddressBook,
    FaTrash,
    FaCogs,
    FaShip,
    FaClipboardList,
    FaTags,
    FaPlus,
    FaMoneyBill,
    FaFolder,
    FaGavel,
    FaTasks,
    FaFileMedical,
    FaFileInvoice,
    FaFileInvoiceDollar,
    FaSearch,
    FaFilter,
    FaCalculator,
    FaLayerGroup
} from 'react-icons/fa';
import { MdOutlineDescription, MdOutlineUpdate } from 'react-icons/md';
import './manualPage.css';

const api = axios.create({
    baseURL: 'https://hms.backend.hiragroupcloud.com/api',
    headers: { 'Content-Type': 'application/json' }
});

// ✅ FIX: Har request ke saath accessToken automatically bhejo
// (pehle token nahi ja raha tha, isliye backend "Access token required" de raha tha)
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const ManualsPage = () => {
    const [manual, setManual] = useState(null);
    const [loading, setLoading] = useState(true);
    const [videoErrors, setVideoErrors] = useState({});
    const contentRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const videosSectionRef = useRef(null);
    const hasMarkedAsViewed = useRef(false);
    
    // Sidebar state
    const [activeHeading, setActiveHeading] = useState('introduction');
    const [expandedHeadings, setExpandedHeadings] = useState({});
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Scroll gradient indicators
    const [showTopGradient, setShowTopGradient] = useState(false);
    const [showBottomGradient, setShowBottomGradient] = useState(false);

    // Define the headings structure
    const headingsStructure = [
        {
            id: 'introduction',
            text: 'Introduction',
            icon: <FaBookOpen />,
            level: 'h1',
            color: '#4A90D9'
        },
        {
            id: 'dashboard-overview',
            text: 'Dashboard Overview',
            icon: <FaTachometerAlt />,
            level: 'h2',
            color: '#5B9BD5',
            children: [
                { id: 'dashboard-sections', text: 'Dashboard Sections', icon: <FaChartBar />, level: 'h3', color: '#6BA5D9' },
                { id: 'header-section', text: 'Header Section', icon: <FaHome />, level: 'h3', color: '#6BA5D9' },
                { id: 'event-calendar', text: 'Event Calendar', icon: <FaCalendarAlt />, level: 'h3', color: '#6BA5D9' },
                { id: 'at-a-glance', text: 'At a Glance Section', icon: <FaChartBar />, level: 'h3', color: '#6BA5D9' },
                { id: 'cases-tab', text: 'Cases Tab', icon: <FaBriefcase />, level: 'h3', color: '#6BA5D9' },
                { id: 'finances-tab', text: 'Finances Tab', icon: <FaDollarSign />, level: 'h3', color: '#6BA5D9' },
                { id: 'alerts-activity', text: 'Alerts & Activity', icon: <FaBell />, level: 'h3', color: '#6BA5D9' }
            ]
        },
        {
            id: 'navigation-bar',
            text: 'Navigation Bar',
            icon: <FaCompass />,
            level: 'h2',
            color: '#F4A460'
        },
        {
            id: 'settings-module',
            text: 'Settings Module',
            icon: <FaCog />,
            level: 'h2',
            color: '#E74C3C',
            children: [
                {
                    id: 'user-management',
                    text: 'User Management',
                    icon: <FaUsers />,
                    level: 'h3',
                    color: '#E95D4F',
                    children: [
                        { id: 'user-table', text: 'User Table', icon: <FaList />, level: 'h4', color: '#EB6D60' },
                        { id: 'editing-users', text: 'Editing Users', icon: <FaUserEdit />, level: 'h4', color: '#EB6D60' },
                        { id: 'adding-users', text: 'Adding New Users', icon: <FaUserPlus />, level: 'h4', color: '#EB6D60' }
                    ]
                }
            ]
        },
        {
            id: 'accounts-module',
            text: 'Accounts Module',
            icon: <FaBuilding />,
            level: 'h2',
            color: '#27AE60',
            children: [
                { id: 'creating-accounts', text: 'Creating Accounts', icon: <FaPlus />, level: 'h3', color: '#2FC272' },
                { id: 'contacts-management', text: 'Contacts Management', icon: <FaAddressBook />, level: 'h3', color: '#2FC272' },
                { id: 'editing-deletion', text: 'Editing & Deletion', icon: <FaTrash />, level: 'h3', color: '#2FC272' }
            ]
        },
        {
            id: 'case-settings',
            text: 'Case Settings',
            icon: <FaCogs />,
            level: 'h2',
            color: '#8E44AD',
            children: [
                { id: 'shipping-settings', text: 'Shipping Settings', icon: <FaShip />, level: 'h3', color: '#9B59B6' },
                { id: 'procedure-management', text: 'Procedure Management', icon: <FaClipboardList />, level: 'h3', color: '#9B59B6' },
                { id: 'display-categories', text: 'Display Categories', icon: <FaTags />, level: 'h3', color: '#9B59B6' },
                { id: 'adding-procedures', text: 'Adding Procedures', icon: <FaPlus />, level: 'h3', color: '#9B59B6' },
                { id: 'pricing-management', text: 'Pricing Management', icon: <FaMoneyBill />, level: 'h3', color: '#9B59B6' }
            ]
        },
        {
            id: 'cases-module',
            text: 'Cases Module',
            icon: <FaFolder />,
            level: 'h2',
            color: '#E67E22',
            children: [
                { id: 'creating-case', text: 'Creating a Case', icon: <FaPlus />, level: 'h3', color: '#E88D3F' },
                { id: 'case-actions', text: 'Case Actions', icon: <FaTasks />, level: 'h3', color: '#E88D3F' },
                { id: 'additional-case-function', text: 'Additional Functions', icon: <FaGavel />, level: 'h3', color: '#E88D3F' },
                { id: 'procedure-file-management', text: 'Procedure & Files', icon: <FaFileMedical />, level: 'h3', color: '#E88D3F' },
                {
                    id: 'invoice-management',
                    text: 'Invoice Management',
                    icon: <FaFileInvoiceDollar />,
                    level: 'h3',
                    color: '#E88D3F',
                    children: [
                        { id: 'creating-invoice', text: 'Creating an Invoice', icon: <FaFileInvoice />, level: 'h4', color: '#EB9D5C' },
                        { id: 'invoice-functions', text: 'Invoice Functions', icon: <FaTasks />, level: 'h4', color: '#EB9D5C' }
                    ]
                },
                {
                    id: 'monthly-case-view',
                    text: 'Monthly Case View',
                    icon: <FaCalendarAlt />,
                    level: 'h3',
                    color: '#E88D3F',
                    children: [
                        { id: 'available-filters', text: 'Available Filters', icon: <FaFilter />, level: 'h4', color: '#EB9D5C' },
                        { id: 'financial-calculations', text: 'Financial Calculations', icon: <FaCalculator />, level: 'h4', color: '#EB9D5C' }
                    ]
                },
                { id: 'additional-case-views', text: 'Additional Views', icon: <FaSearch />, level: 'h3', color: '#E88D3F' }
            ]
        },
        {
            id: 'training-videos',
            text: 'Training Videos',
            icon: <FaVideo />,
            level: 'h2',
            color: '#3498DB'
        }
    ];

    useEffect(() => {
        fetchManual();
    }, []);

    // Show only Introduction after content loads - ALL TEXT LEFT ALIGNED
    useEffect(() => {
        if (manual?.content && contentRef.current) {
            setTimeout(() => {
                showOnlyIntroduction();
            }, 300);
        }
    }, [manual?.content]);

    // Handle scroll gradient indicators
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            setShowTopGradient(scrollTop > 10);
            setShowBottomGradient(scrollTop + clientHeight < scrollHeight - 10);
        };

        handleScroll();
        
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [manual?.content]);

    const showOnlyIntroduction = () => {
        if (!contentRef.current) return;
        
        const container = contentRef.current;
        const allHeadings = container.querySelectorAll('h1, h2, h3');
        
        let foundIntroduction = false;
        let hideRest = false;
        
        allHeadings.forEach((heading) => {
            const text = heading.textContent.trim().toLowerCase();
            
            if (text.includes('introduction')) {
                foundIntroduction = true;
                hideRest = false;
                
                heading.style.textAlign = 'left';
                heading.style.fontSize = '36px';
                heading.style.fontWeight = 'bold';
                heading.style.margin = '80px 0 40px 0';
                heading.style.display = 'block';
                heading.style.color = '#1a202c';
                
                let nextEl = heading.nextElementSibling;
                if (nextEl && nextEl.tagName === 'P') {
                    nextEl.style.textAlign = 'left';
                    nextEl.style.color = '#64748b';
                    nextEl.style.fontSize = '16px';
                }
                
                return;
            }
            
            if (foundIntroduction && !hideRest) {
                hideRest = true;
            }
            
            if (hideRest || !foundIntroduction) {
                heading.style.display = 'none';
            }
        });
        
        if (foundIntroduction) {
            const allElements = container.querySelectorAll('*');
            let startHiding = false;
            
            allElements.forEach((el) => {
                const text = el.textContent.trim().toLowerCase();
                
                if (text === 'introduction' && el.tagName.match(/^H[1-6]$/)) {
                    startHiding = false;
                    return;
                }
                
                if (text.includes('dashboard overview') || 
                    text.includes('navigation bar') ||
                    text.includes('settings module') ||
                    text.includes('accounts module') ||
                    text.includes('case settings') ||
                    text.includes('cases module')) {
                    startHiding = true;
                }
                
                if (startHiding && el !== container && el.parentElement !== container) {
                    el.style.display = 'none';
                }
            });
        }
    };

    const fetchManual = async () => {
        try {
            const response = await api.get('/manuals');
            if (response.data.success) {
                setManual(response.data.data);
                if (!hasMarkedAsViewed.current) {
                    localStorage.setItem('manualLastViewedVersion', response.data.data.version);
                    hasMarkedAsViewed.current = true;
                }
            }
        } catch (error) {
            console.error('Error fetching manual:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleHeading = (headingId) => {
        setExpandedHeadings(prev => ({
            ...prev,
            [headingId]: !prev[headingId]
        }));
    };

    const findParentIds = (items, targetId, parents = []) => {
        for (let item of items) {
            if (item.id === targetId) return parents;
            if (item.children) {
                const result = findParentIds(item.children, targetId, [...parents, item.id]);
                if (result) return result;
            }
        }
        return null;
    };

    const findHeadingInfo = (items, id) => {
        for (let item of items) {
            if (item.id === id) return item;
            if (item.children) {
                const found = findHeadingInfo(item.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const scrollToSection = (headingId) => {
        const headingInfo = findHeadingInfo(headingsStructure, headingId);
        if (!headingInfo) return;
        
        const searchText = headingInfo.text;
        setActiveHeading(headingId);
        
        const parentIds = findParentIds(headingsStructure, headingId);
        if (parentIds) {
            setExpandedHeadings(prev => {
                const newExpanded = { ...prev };
                parentIds.forEach(id => { newExpanded[id] = true; });
                return newExpanded;
            });
        }
        
        // Special handling for Training Videos section
        if (headingId === 'training-videos') {
            if (videosSectionRef.current) {
                setTimeout(() => {
                    if (!videosSectionRef.current) return;
                    videosSectionRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                    
                    videosSectionRef.current.style.backgroundColor = '#F0F4FF';
                    videosSectionRef.current.style.transition = 'background-color 0.5s ease';
                    videosSectionRef.current.style.borderRadius = '12px';
                    videosSectionRef.current.style.padding = '20px';
                    
                    setTimeout(() => {
                        if (videosSectionRef.current) {
                            videosSectionRef.current.style.backgroundColor = 'transparent';
                            videosSectionRef.current.style.padding = '0';
                        }
                    }, 2000);
                }, 100);
            }
            return;
        }
        
        // Make all hidden content visible again with LEFT alignment
        if (contentRef.current) {
            const allElements = contentRef.current.querySelectorAll('*');
            allElements.forEach(el => {
                el.style.display = '';
                el.style.textAlign = 'left';
                el.style.fontSize = '';
                el.style.fontWeight = '';
                el.style.margin = '';
                el.style.color = '';
            });
        }
        
        setTimeout(() => {
            if (!contentRef.current) return;
            
            let targetElement = document.getElementById(headingId);
            
            if (!targetElement) {
                const allHeadings = contentRef.current.querySelectorAll('h1, h2, h3, h4, h5, h6');
                
                for (let heading of allHeadings) {
                    const headingText = heading.textContent.trim();
                    
                    if (headingText.toLowerCase() === searchText.toLowerCase()) {
                        targetElement = heading;
                        heading.id = headingId;
                        break;
                    }
                    
                    if (headingText.toLowerCase().includes(searchText.toLowerCase()) ||
                        searchText.toLowerCase().includes(headingText.toLowerCase())) {
                        targetElement = heading;
                        heading.id = headingId;
                        break;
                    }
                }
            }
            
            if (!targetElement) {
                const allElements = contentRef.current.querySelectorAll('*');
                
                for (let el of allElements) {
                    if (el.children.length === 0) {
                        const elText = el.textContent.trim();
                        if (elText.toLowerCase() === searchText.toLowerCase()) {
                            targetElement = el.parentElement;
                            break;
                        }
                    }
                }
            }
            
            if (targetElement && scrollContainerRef.current) {
                setTimeout(() => {
                    const container = scrollContainerRef.current;
                    if (!container) return;
                    const containerRect = container.getBoundingClientRect();
                    const targetRect = targetElement.getBoundingClientRect();
                    
                    const scrollTop = targetRect.top - containerRect.top + container.scrollTop - 20;
                    
                    container.scrollTo({
                        top: scrollTop,
                        behavior: 'smooth'
                    });
                    
                    targetElement.style.backgroundColor = '#F0F4FF';
                    targetElement.style.transition = 'background-color 0.3s ease';
                    targetElement.style.borderRadius = '6px';
                    targetElement.style.padding = '8px 12px';
                    
                    setTimeout(() => {
                        targetElement.style.backgroundColor = 'transparent';
                    }, 2000);
                }, 100);
            }
        }, 200);
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    // Close button inside sidebar: mobile closes the drawer, desktop collapses the sidebar
    const handleSidebarClose = () => {
        if (window.innerWidth <= 768) {
            setMobileSidebarOpen(false);
        } else {
            toggleSidebar();
        }
    };

    const getRelativeTime = (date) => {
        const now = new Date();
        const updated = new Date(date);
        const diffMs = now - updated;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} mins ago`;
        if (diffHours < 24) return `${diffHours} hrs ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        return updated.toLocaleDateString();
    };

    const getVideoUrl = (video) => {
        if (!video || !video.url) return '';
        const baseUrl = 'https://hms.backend.hiragroupcloud.com';
        let videoUrl = video.url;
        if (!videoUrl.startsWith('http')) {
            videoUrl = videoUrl.startsWith('/') ? videoUrl : '/' + videoUrl;
            videoUrl = baseUrl + videoUrl;
        }
        return videoUrl;
    };

    const openVideoInNewTab = (video) => {
        const videoUrl = getVideoUrl(video);
        if (videoUrl) {
            window.open(videoUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleVideoError = (videoId) => {
        setVideoErrors(prev => ({ ...prev, [videoId]: true }));
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
    };

    const filterHeadings = (items, term) => {
        if (!term) return items;
        return items.reduce((acc, item) => {
            const matchesSearch = item.text.toLowerCase().includes(term.toLowerCase());
            const filteredChildren = item.children ? filterHeadings(item.children, term) : [];
            if (matchesSearch || filteredChildren.length > 0) {
                acc.push({ ...item, children: filteredChildren.length > 0 ? filteredChildren : item.children });
            }
            return acc;
        }, []);
    };

    const renderHeadings = (items, depth = 0) => {
        const filteredItems = searchTerm ? filterHeadings(items, searchTerm) : items;
        
        return filteredItems.map((item) => (
            <li 
                key={item.id}
                className={`heading-item ${item.level} ${activeHeading === item.id ? 'active' : ''} ${expandedHeadings[item.id] ? 'expanded' : ''}`}
            >
                <div 
                    className="heading-link"
                    onClick={() => {
                        scrollToSection(item.id);
                        if (item.children && item.children.length > 0) {
                            toggleHeading(item.id);
                        } else if (window.innerWidth <= 768) {
                            // On mobile, close the drawer after choosing a final section
                            setMobileSidebarOpen(false);
                        }
                    }}
                >
                    <span className="heading-indicator" style={{ background: item.color }}></span>
                    <span className="heading-icon-wrapper" style={{ color: item.color }}>
                        {item.icon}
                    </span>
                    <span className="heading-text">{item.text}</span>
                    {item.children && item.children.length > 0 && (
                        <span className="chevron-icon">
                            {expandedHeadings[item.id] ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                    )}
                </div>
                
                {item.children && item.children.length > 0 && expandedHeadings[item.id] && (
                    <ul className="sub-headings-list">
                        {renderHeadings(item.children, depth + 1)}
                    </ul>
                )}
            </li>
        ));
    };

    const getActiveSectionName = () => {
        if (!activeHeading) return null;
        const info = findHeadingInfo(headingsStructure, activeHeading);
        return info ? info.text : null;
    };

    const activeSectionName = getActiveSectionName();

    return (
        <div className="manual-page">
            <div className="manual-layout">
                <button
                    className={`mobile-sidebar-toggle ${mobileSidebarOpen ? 'is-open' : ''}`}
                    onClick={toggleMobileSidebar}
                >
                    {mobileSidebarOpen ? <FaTimes /> : <FaBars />}
                </button>

                <aside className={`manual-sidebar ${sidebarOpen ? 'open' : 'closed'} ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-header-content">
                            <div className="sidebar-logo">
                                <FaLayerGroup className="logo-icon" />
                            </div>
                            <div className="sidebar-title-group">
                                <h3>Contents</h3>
                                <p>Navigation Guide</p>
                            </div>
                        </div>
                        <button className="sidebar-close-btn" onClick={handleSidebarClose}>
                            <FaTimes />
                        </button>
                    </div>

                    <div className="sidebar-search">
                        <div className="search-input-wrapper">
                            <FaSearch className="search-icon" />
                            <input 
                                type="text" 
                                placeholder="Search sections..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="search-clear" onClick={() => setSearchTerm('')}>
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <nav className="sidebar-nav">
                        <div className="nav-section-label">
                            <span>TABLE OF CONTENTS</span>
                        </div>
                        <ul className="headings-list">
                            {renderHeadings(headingsStructure)}
                        </ul>
                    </nav>

                    <div className="sidebar-footer">
                        <div className="sidebar-footer-content">
                            <span>OSHMS</span>
                        </div>
                    </div>
                </aside>

                {mobileSidebarOpen && (
                    <div className="sidebar-overlay" onClick={toggleMobileSidebar}></div>
                )}

                <main className={`manual-main ${sidebarOpen ? '' : 'sidebar-closed'}`}>
                    <div className="manual-container-fluid">
                        <div className="manual-content-wrapper">
                            <div className="manual-header-section">
                                <div className="header-top">
                                    <h2>{manual?.title || 'Documentation'}</h2>
                                    {activeSectionName && (
                                        <div className="current-section-badge">
                                           {activeSectionName}
                                        </div>
                                    )}
                                </div>
                                <div className="manual-meta-grid">
                                    <div className="meta-item">
                                        <FaCalendarAlt className="meta-icon" />
                                        <div>
                                            <span className="meta-label">Last Updated</span>
                                            <span className="meta-value">
                                                {manual?.updatedAt ? new Date(manual.updatedAt).toLocaleDateString('en-PK', {
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                }) : 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="meta-item">
                                        <FaClock className="meta-icon" />
                                        <div>
                                            <span className="meta-label">Updated</span>
                                            <span className="meta-value">{manual?.updatedAt ? getRelativeTime(manual.updatedAt) : 'N/A'}</span>
                                        </div>
                                    </div>
                                    <div className="meta-item">
                                        <MdOutlineUpdate className="meta-icon" />
                                        <div>
                                            <span className="meta-label">Status</span>
                                            <span className="meta-value status-badge">Active</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="manual-body-section">
                                <div className="content-header">
                                    <MdOutlineDescription className="section-icon" />
                                    <h3>Documentation</h3>
                                </div>
                                
                                {/* Scrollable Documentation Container */}
                                <div className="manual-content-container">
                                    <div 
                                        ref={scrollContainerRef}
                                        className={`documentation-scroll-container ${
                                            showTopGradient ? 'scrollable-top' : ''
                                        } ${
                                            showBottomGradient ? 'scrollable-bottom' : ''
                                        }`}
                                    >
                                        <div 
                                            ref={contentRef}
                                            className="manual-content"
                                        >
                                            <div 
                                                className="tiptap-content"
                                                dangerouslySetInnerHTML={{ __html: manual?.content || (loading ? '' : '<p>No content available</p>') }} 
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Training Videos Section */}
                            <div ref={videosSectionRef} className="videos-section" id="training-videos">
                                {loading ? null : manual?.videos && manual.videos.length > 0 ? (
                                    <>
                                        <div className="content-header">
                                            <FaVideo className="section-icon" />
                                            <h3>Training Videos ({manual.videos.length})</h3>
                                        </div>
                                        <div className="videos-grid">
                                            {manual.videos.map((video) => {
                                                const videoUrl = getVideoUrl(video);
                                                const hasError = videoErrors[video._id];
                                                
                                                return (
                                                    <div key={video._id} className="video-card">
                                                        <div className="video-thumbnail">
                                                            {!hasError ? (
                                                                <video 
                                                                    className="video-preview"
                                                                    src={videoUrl}
                                                                    onError={() => handleVideoError(video._id)}
                                                                    preload="metadata"
                                                                />
                                                            ) : (
                                                                <div className="video-error-thumbnail">
                                                                    <FaExclamationTriangle className="error-icon" />
                                                                    <p>Video unavailable</p>
                                                                </div>
                                                            )}
                                                            <div className="video-play-overlay">
                                                                <FaPlayCircle className="play-icon-large" />
                                                            </div>
                                                        </div>
                                                        <div className="video-card-content">
                                                            <h4>{video.title}</h4>
                                                            {video.description && <p>{video.description}</p>}
                                                            <div className="video-meta">
                                                                <div className="meta-item-mini">
                                                                    <FaFileAlt className="mini-icon" />
                                                                    <span>{formatFileSize(video.size)}</span>
                                                                </div>
                                                                <div className="meta-item-mini">
                                                                    <FaRegClock className="mini-icon" />
                                                                    <span>Uploaded: {video.uploadedAt ? new Date(video.uploadedAt).toLocaleDateString() : 'N/A'}</span>
                                                                </div>
                                                            </div>
                                                            <button 
                                                                onClick={() => openVideoInNewTab(video)}
                                                                className="btn-watch-video"
                                                            >
                                                                <FaExternalLinkAlt /> Watch Video
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="content-header">
                                            <FaVideo className="section-icon" />
                                            <h3>Training Videos</h3>
                                        </div>
                                        <div className="no-videos-message">
                                            <p>No training videos available yet.</p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="manual-footer-section">
                                <div className="footer-note">
                                    <p>📌 This Manual is regularly updated.</p>
                                </div>
                                <div className="footer-meta">
                                    <span>Generated: {new Date().toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default ManualsPage;